import { degreesToRadians, radiansToDegrees } from '@turf/turf';
import classNames from 'classnames/bind';
import commaNumber from 'comma-number';
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { RiCheckLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';

import styles from './common.module.scss';

import actions from '@/actions';
import FieldRow from '@/components/ui/FieldRow';
import Checkbox from '@/components/ui/kit/Checkbox';
import { FieldNumber, FieldText } from '@/components/ui/kit/Field';
import Select from '@/components/ui/kit/Select';
import {
  SURVEY_ENTRIES,
  SURVEY_GAP_MIN,
  SURVEY_GAP_MAX,
  SURVEY_TURNAROUND_MIN,
  SURVEY_TURNAROUND_MAX,
  SHOOT_OVERLAP_DEFAULT_OPTIONS,
  SHOOT_OVERLAP_MIN,
  SHOOT_OVERLAP_MAX,
} from '@/config';
import { CAMERAS } from '@/define/camera';
import { NotifierService as notifier } from '@/libs/Notifier';
import { getDistance, getDistanceAlongPath, getPolygonArea } from '@/utils/MapUtils';
import { deepCopy, nestedAssign } from '@/utils/ObjectUtils';
import { getPositions } from '@/utils/SurveyCalculator';

const cx = classNames.bind(styles);

const Command = ({ index, data: command }) => {
  const shoots = useSelector((state) => state.editor.shoots);
  const dispatch = useDispatch();
  const [camera, setCamera] = useState();
  const [focalLength, setFocalLength] = useState();
  const domRefs = useRef({
    relAltitude: useRef(),
    gap: useRef(),
    turnaround: {
      before: useRef(),
      after: useRef(),
    },
    rotate: useRef(),
    segmentDistance: useRef(),
    overlap: {
      forward: useRef(),
      side: useRef(),
    },
  }).current;

  // 경로 분할 사용여부
  const hasSegment = useMemo(() => {
    return command.data.segment !== null;
  }, [command.data.segment]);

  // 카메라 사용여부
  const hasCamera = useMemo(() => {
    return command.data.camera !== null;
  }, [command.data.camera]);

  // 면적
  const area = useMemo(() => {
    return getPolygonArea(command.data.boundary);
  }, [command.data.positions]);

  // 전체 경로 거리
  const totalDistance = useMemo(() => {
    return getDistanceAlongPath(command.data.positions);
  }, [command.data.positions]);

  // 분할 경로 내역
  const segments = useMemo(() => {
    if (!hasSegment) return;

    const positionGroups = command.data.positions.reduce((acc, position) => {
      const { segment } = position;

      if (!acc[segment]) {
        acc[segment] = [];
      }
      acc[segment].push(position);

      return acc;
    }, []);

    const distances = positionGroups.map((positions) => getDistanceAlongPath(positions));

    return distances.map((distance, index) => {
      // 누적 경로 거리
      let accumulatedDistance = 0;
      for (let i = 0; i < index; i++) {
        accumulatedDistance += distances[i];
      }
      const left = `${(accumulatedDistance / totalDistance) * 100}%`;
      let right = `${(1 - (accumulatedDistance + distance) / totalDistance) * 100}%`;
      if (distances.length - 1 === index) {
        right = '0%';
      }

      return {
        label: `${index + 1}`.padStart(3, '0'),
        distance,
        left,
        right,
      };
    });
  }, [command.data.segment]);

  // 경로 분할 거리 최소값, 최대값 정의
  const segmentDistanceRange = useMemo(() => {
    if (!hasSegment) return;

    // 최소값: 단위 경로별 거리 중 최대값
    const min = command.data.positions.slice(1).reduce((max, position, index) => {
      const distance = getDistance(command.data.positions[index], position);
      return Math.max(max, distance);
    }, 0);
    // 최대값: 전체 경로 거리
    const max = totalDistance;

    return { min, max };
  }, [hasSegment]);

  // 촬영 수
  const shootCount = useMemo(() => {
    return shoots.filter(({ id }) => id.startsWith(command.id)).length;
  }, [shoots]);

  useEffect(() => {
    setFieldsToOrigin();
  }, [command.data]);

  const setFieldsToOrigin = () => {
    domRefs.relAltitude.current.value = command.data.altitude.toFixed(1);
    domRefs.gap.current.value = command.data.gap.toFixed(1);
    domRefs.turnaround.before.current.value = command.data.turnaround.before.toFixed(1);
    domRefs.turnaround.after.current.value = command.data.turnaround.after.toFixed(1);
    domRefs.rotate.current.value = command.data.rotate;

    if (hasSegment) {
      domRefs.segmentDistance.current.value = command.data.segment.distance.toFixed(1);
    } else {
      domRefs.segmentDistance.current.value = '';
    }

    if (hasCamera) {
      setCamera(command.data.camera.name);
      setFocalLength(command.data.camera.options.focalLength);
      domRefs.overlap.forward.current.value = command.data.camera.options.overlap.forward.toFixed(1);
      domRefs.overlap.side.current.value = command.data.camera.options.overlap.side.toFixed(1);
    } else {
      setCamera('None');
    }
  };

  const rotateEntryPoint = () => {
    const entryIndex = (command.data.entryIndex + 1) % SURVEY_ENTRIES.length;
    const positions = getPositionsByChangedOption({ entryIndex });

    editSurvey({ entryIndex, positions });
  };

  const changeRelAltitude = (e) => {
    const altitude = Number(e.target.value);

    if (hasCamera) {
      const camera = deepCopy(command.data.camera);
      camera.interval = getDistanceByOptions(altitude, camera.options.aov.vertical, camera.options.overlap.forward);

      const gap = getDistanceByOptions(
        altitude,
        command.data.camera.options.aov.horizontal,
        command.data.camera.options.overlap.side
      );
      const positions = getPositionsByChangedOption({ gap });

      editSurvey({ altitude, gap, positions, camera });
    } else {
      editSurvey({ altitude });
    }
  };

  const changeGap = (e) => {
    let gap = Number(e.target.value);
    gap = Math.max(gap, SURVEY_GAP_MIN);
    gap = Math.min(gap, SURVEY_GAP_MAX);

    const positions = getPositionsByChangedOption({ gap });
    editSurvey({ gap, positions });
  };

  const changeTurnaround = (e) => {
    const name = e.target.name.split('.')[1];

    let value = Number(e.target.value);
    value = Math.max(value, SURVEY_TURNAROUND_MIN);
    value = Math.min(value, SURVEY_TURNAROUND_MAX);

    const turnaround = deepCopy(command.data.turnaround);
    turnaround[name] = value;
    const positions = getPositionsByChangedOption({ turnaround });
    editSurvey({ turnaround, positions });
  };

  const changeRotate = (e) => {
    let rotate = Math.trunc(e.target.value) % 360;
    if (rotate < 0) {
      rotate = 360 - Math.abs(rotate);
    }
    rotate %= 180;

    const positions = getPositionsByChangedOption({ rotate });
    editSurvey({ rotate, positions });
  };

  const changeSegmentDistance = (e) => {
    let distance = Number(e.target.value);
    distance = Math.max(distance, segmentDistanceRange.min);
    distance = Math.min(distance, segmentDistanceRange.max);

    const segment = { index: 0, distance };
    const positions = getPositionsByChangedOption({ segment });
    editSurvey({ positions, segment });
  };

  const selectSegment = (index) => {
    const segment = { ...command.data.segment, index };
    editSurvey({ segment });
  };

  const changeCamera = (camera) => {
    const focalLength = camera.specs.focalLengths[0];
    const aov = {
      vertical: getAov(camera.specs.sensorSize.height, focalLength),
      horizontal: getAov(camera.specs.sensorSize.width, focalLength),
    };
    const interval = getDistanceByOptions(command.data.altitude, aov.vertical, SHOOT_OVERLAP_DEFAULT_OPTIONS.forward);
    const gap = getDistanceByOptions(command.data.altitude, aov.horizontal, SHOOT_OVERLAP_DEFAULT_OPTIONS.side);
    const positions = getPositionsByChangedOption({ gap, camera });

    editSurvey({
      gap,
      positions,
      camera: {
        name: camera.name,
        interval,
        options: {
          aov,
          focalLength,
          sensorSize: camera.specs.sensorSize,
          overlap: SHOOT_OVERLAP_DEFAULT_OPTIONS,
        },
      },
    });
  };

  const toggleCamera = () => {
    if (hasCamera) {
      const positions = getPositionsByChangedOption({ camera: null });
      editSurvey({ positions, camera: null });
    } else {
      changeCamera(CAMERAS[0]);
    }
  };

  const selectCamera = (e) => {
    const found = CAMERAS.find(({ name }) => name === e.target.value);
    changeCamera(found);
  };

  const selectFocalLength = (e) => {
    const camera = deepCopy(command.data.camera);

    const focalLength = Number(e.target.value);
    const aov = {
      vertical: getAov(camera.options.sensorSize.height, focalLength),
      horizontal: getAov(camera.options.sensorSize.width, focalLength),
    };

    camera.options.aov = aov;
    camera.options.focalLength = focalLength;
    camera.interval = getDistanceByOptions(command.data.altitude, aov.vertical, camera.options.overlap.forward);

    const gap = getDistanceByOptions(command.data.altitude, aov.horizontal, camera.options.overlap.side);
    const positions = getPositionsByChangedOption({ gap, camera });

    editSurvey({ gap, positions, camera });
  };

  const changeOverlap = (e) => {
    const name = e.target.name;

    let value = Number(e.target.value);
    value = Math.max(value, SHOOT_OVERLAP_MIN);
    value = Math.min(value, SHOOT_OVERLAP_MAX);

    const camera = deepCopy(command.data.camera);
    nestedAssign(camera.options, name.split('.'), value);

    if (name.endsWith('forward')) {
      camera.interval = getDistanceByOptions(
        command.data.altitude,
        camera.options.aov.vertical,
        camera.options.overlap.forward
      );
    }

    const gap = getDistanceByOptions(command.data.altitude, camera.options.aov.horizontal, camera.options.overlap.side);
    const positions = getPositionsByChangedOption({ gap });

    editSurvey({ gap, positions, camera });
  };

  const editSurvey = (values) => {
    if (values.positions?.length === 0) {
      notifier.error('The area is too small.');
      setFieldsToOrigin();
      return;
    }

    // 경로 분할 중 경로 분할, 진입점 관련 외 값 수정 시
    let isLockedBySegment = hasSegment;
    isLockedBySegment &&= !Object.hasOwn(values, 'segment');
    isLockedBySegment &&= !Object.hasOwn(values, 'entryIndex');

    if (isLockedBySegment) {
      notifier.error('Please deactivate the segment option to edit survey.');
      setFieldsToOrigin();
      return;
    }

    dispatch(actions.mission.editSurvey(index, values));
  };

  const getAov = (sensorSize, focalLength) => {
    const radian = 2 * Math.atan(sensorSize / (focalLength * 2));
    return radiansToDegrees(radian);
  };

  const getPositionsByChangedOption = (values) => {
    const { boundary, entryIndex, gap, turnaround, rotate, camera, segment } = command.data;

    return getPositions(boundary, {
      entryIndex,
      gap,
      turnaround,
      rotate,
      camera,
      segment,
      ...values,
    });
  };

  const getDistanceByOptions = (altitude, aov, overlap) => {
    const distance = Math.tan(degreesToRadians(aov / 2)) * altitude * 2;
    const value = distance * (1 - overlap / 100);

    return Number(value.toFixed(1));
  };

  const toggleSegment = () => {
    let segment = null;
    if (!hasSegment) {
      const halfIndex = command.data.positions.length / 2;
      const distance = getDistanceAlongPath(command.data.positions.slice(0, halfIndex + 1));

      segment = { index: 0, distance };
    }

    const positions = getPositionsByChangedOption({ segment });
    editSurvey({ positions, segment });
  };

  return (
    <div className={cx('container')}>
      <button type="button" className={cx('button')} onClick={rotateEntryPoint}>
        Rotate Entry Point
      </button>
      <FieldRow label="Area" unit="㎢">
        <FieldNumber value={area.toFixed(1)} readOnly />
      </FieldRow>
      <FieldRow label="Relative Altitude" unit="m">
        <FieldNumber ref={domRefs.relAltitude} name="relAltitude" onBlur={changeRelAltitude} />
      </FieldRow>
      <FieldRow label="Gap" unit="m">
        <FieldNumber ref={domRefs.gap} name="gap" readOnly={hasCamera} onBlur={changeGap} />
      </FieldRow>
      <FieldRow label="Before turnaround" unit="m">
        <FieldNumber ref={domRefs.turnaround.before} name="turnaround.before" onBlur={changeTurnaround} />
      </FieldRow>
      <FieldRow label="After turnaround" unit="m">
        <FieldNumber ref={domRefs.turnaround.after} name="turnaround.after" onBlur={changeTurnaround} />
      </FieldRow>
      <FieldRow label="Rotate" unit="°">
        <FieldNumber ref={domRefs.rotate} name="rotate" onBlur={changeRotate} />
      </FieldRow>
      <FieldRow label="Segment" unit="m">
        <Checkbox checked={hasSegment} onClick={toggleSegment} />
        <FieldNumber
          ref={domRefs.segmentDistance}
          name="segment.distance"
          disabled={!hasSegment}
          onBlur={changeSegmentDistance}
        />
      </FieldRow>
      {hasSegment && (
        <div className={cx('segments')}>
          {segments.map((segment, index) => {
            const { label, distance, left, right } = segment;
            const isChecked = index === command.data.segment.index;

            return (
              <div key={index} className={cx('row')}>
                <label>
                  {label}
                  <div className={cx('gauge')}>
                    <div className={cx(['bar', { active: isChecked }])} style={{ left, right }} />
                  </div>
                  <div className={cx('wrapper')}>
                    <div className={cx('radio')} onClick={() => selectSegment(index)}>
                      {isChecked && <RiCheckLine size={10} color="white" />}
                    </div>
                    <FieldNumber value={distance.toFixed(1)} readOnly className={cx('value')} />
                    <span className={cx('unit')}>m</span>
                  </div>
                </label>
              </div>
            );
          })}
        </div>
      )}
      <FieldRow label="Camera">
        <Checkbox checked={hasCamera} onClick={toggleCamera} />
        <Select value={camera} disabled={!hasCamera} onChange={selectCamera} className={cx('select')}>
          {!hasCamera && <option value="None">None</option>}
          {CAMERAS.map((camera, index) => (
            <option key={index} value={camera.name}>
              {camera.name}
            </option>
          ))}
        </Select>
      </FieldRow>
      {hasCamera && (
        <>
          <FieldRow label="Focal Length" unit="㎜">
            <Select value={focalLength} onChange={selectFocalLength} className={cx('select')}>
              {CAMERAS.find(({ name }) => name === command.data.camera.name).specs.focalLengths.map(
                (focalLength, index) => (
                  <option key={index} value={focalLength}>
                    {focalLength}
                  </option>
                )
              )}
            </Select>
          </FieldRow>
          <FieldRow label="Sensor Size" unit="㎜">
            <FieldText
              value={`${command.data.camera.options.sensorSize.width} x ${command.data.camera.options.sensorSize.height}`}
              readOnly
            />
          </FieldRow>
          <FieldRow label="Forward Overlap" unit="%">
            <FieldNumber ref={domRefs.overlap.forward} name="overlap.forward" onBlur={changeOverlap} />
          </FieldRow>
          <FieldRow label="Side Overlap" unit="%">
            <FieldNumber ref={domRefs.overlap.side} name="overlap.side" onBlur={changeOverlap} />
          </FieldRow>
          <FieldRow label="Shoot Interval" unit="m">
            <FieldNumber value={command.data.camera.interval} readOnly />
          </FieldRow>
          <FieldRow label="Shoot Count">
            <FieldText value={commaNumber(shootCount)} readOnly />
          </FieldRow>
        </>
      )}
    </div>
  );
};

export default Command;
